/**
 * LockIndicator Component
 *
 * Displays the current lock status for collaborative editing.
 * Shows who holds the lock if it's another user.
 */

import { Lock, LockOpen, AlertCircle, Loader2, UserX } from "lucide-react";
import type { ReactElement } from "react";

import type { LockStatus, LockInfo } from "../../hooks/use-draft-management";

// ============================================================================
// Types
// ============================================================================

/** Lock indicator props */
export interface LockIndicatorProps {
  /** Current lock status */
  readonly status: LockStatus;
  /** Lock info when locked */
  readonly lockInfo?: LockInfo | null;
  /** Info about who holds the lock if not the current user */
  readonly heldByInfo?:
    | { name?: string | undefined; email?: string | undefined; userId: string }
    | null
    | undefined;
  /** Show expanded view with details */
  readonly expanded?: boolean;
  /** Additional CSS classes */
  readonly className?: string;
  /** Click handler for acquiring lock */
  readonly onAcquire?: () => void;
  /** Click handler for releasing lock */
  readonly onRelease?: () => void;
}

// ============================================================================
// Status Configuration
// ============================================================================

interface StatusConfig {
  readonly icon: typeof Lock;
  readonly label: string;
  readonly bgColor: string;
  readonly textColor: string;
  readonly iconColor: string;
  readonly showPulse?: boolean;
}

const STATUS_CONFIG: Record<LockStatus, StatusConfig> = {
  unlocked: {
    icon: LockOpen,
    label: "Unlocked",
    bgColor: "bg-gray-100",
    textColor: "text-gray-700",
    iconColor: "text-gray-500",
  },
  acquiring: {
    icon: Loader2,
    label: "Acquiring lock...",
    bgColor: "bg-blue-50",
    textColor: "text-blue-700",
    iconColor: "text-blue-500",
    showPulse: true,
  },
  locked: {
    icon: Lock,
    label: "Editing",
    bgColor: "bg-green-50",
    textColor: "text-green-700",
    iconColor: "text-green-500",
  },
  locked_by_other: {
    icon: UserX,
    label: "Locked by another user",
    bgColor: "bg-amber-50",
    textColor: "text-amber-700",
    iconColor: "text-amber-500",
  },
  error: {
    icon: AlertCircle,
    label: "Lock error",
    bgColor: "bg-red-50",
    textColor: "text-red-700",
    iconColor: "text-red-500",
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * LockIndicator - Shows collaborative editing lock status
 *
 * CSS Classes:
 * - lock-indicator: Container element
 * - lock-indicator--unlocked: Unlocked state
 * - lock-indicator--acquiring: Acquiring state
 * - lock-indicator--locked: Locked (by current user) state
 * - lock-indicator--locked-by-other: Locked by another user state
 * - lock-indicator--error: Error state
 */
export function LockIndicator({
  status,
  lockInfo,
  heldByInfo,
  expanded = false,
  className = "",
  onAcquire,
  onRelease,
}: LockIndicatorProps): ReactElement {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const isSpinning = status === "acquiring";

  // Format who holds the lock
  const heldByDisplay =
    heldByInfo?.name ?? heldByInfo?.email ?? heldByInfo?.userId ?? "another user";

  // Format expiration time
  const expiresAt = lockInfo?.expiresAt;
  const expiresIn = expiresAt ? formatTimeUntil(new Date(expiresAt)) : undefined;

  // Determine if we can show action buttons
  const canAcquire = status === "unlocked" || status === "error";
  const canRelease = status === "locked";

  return (
    <div
      className={`lock-indicator lock-indicator--${status.replace("_", "-")} inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${config.bgColor} ${config.textColor} ${className}`}
    >
      {/* Status Icon */}
      <Icon
        className={`h-4 w-4 ${config.iconColor} ${isSpinning ? "animate-spin" : ""}`}
        aria-hidden="true"
      />

      {/* Status Label */}
      <span className="font-medium">
        {status === "locked_by_other" ? `Locked by ${heldByDisplay}` : config.label}
      </span>

      {/* Expanded Details */}
      {expanded && status === "locked" && expiresIn && (
        <span className="text-xs opacity-75">(expires in {expiresIn})</span>
      )}

      {/* Action Buttons */}
      {expanded && (
        <>
          {canAcquire && onAcquire && (
            <button
              type="button"
              onClick={onAcquire}
              className="ml-2 rounded bg-blue-500 px-2 py-0.5 text-xs font-medium text-white hover:bg-blue-600 transition-colors"
            >
              Acquire Lock
            </button>
          )}
          {canRelease && onRelease && (
            <button
              type="button"
              onClick={onRelease}
              className="ml-2 rounded bg-gray-500 px-2 py-0.5 text-xs font-medium text-white hover:bg-gray-600 transition-colors"
            >
              Release
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Compact Variant
// ============================================================================

/**
 * Compact lock indicator showing just an icon with tooltip.
 */
export function LockIndicatorCompact({
  status,
  heldByInfo,
  className = "",
}: Pick<LockIndicatorProps, "status" | "heldByInfo" | "className">): ReactElement {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const isSpinning = status === "acquiring";

  const heldByDisplay =
    heldByInfo?.name ?? heldByInfo?.email ?? heldByInfo?.userId ?? "another user";
  const title = status === "locked_by_other" ? `Locked by ${heldByDisplay}` : config.label;

  return (
    <div
      className={`lock-indicator-compact inline-flex items-center justify-center rounded-full p-1.5 ${config.bgColor} ${className}`}
      title={title}
      role="status"
      aria-label={title}
    >
      <Icon
        className={`h-4 w-4 ${config.iconColor} ${isSpinning ? "animate-spin" : ""}`}
        aria-hidden="true"
      />
    </div>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format time until a date as a human-readable string.
 */
function formatTimeUntil(date: Date): string {
  const now = new Date();
  const diff = date.getTime() - now.getTime();

  if (diff <= 0) {
    return "expired";
  }

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);

  if (minutes >= 1) {
    return `${minutes}m`;
  }

  return `${seconds}s`;
}

export default LockIndicator;
